// NewsBlackoutsPage — News trading blackouts & upcoming events
// Per Section 13.9: no trading within 2 minutes of high-impact news events
// Live countdown timer for next event

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow } from '../../components/TfPrimitives';

interface NewsEvent {
  id: string;
  date: string;       // ISO
  time: string;       // display
  title: string;
  impact: 'high' | 'medium' | 'low';
  currency: 'USD' | 'EUR' | 'GBP' | 'JPY';
  forecast?: string;
  previous?: string;
  actual?: string;
  affectedPairs: string[];
}

const EVENTS: NewsEvent[] = [
  { id: 'e1', date: '2026-07-24T12:30:00Z', time: '08:30 ET', title: 'Initial Jobless Claims',  impact: 'medium', currency: 'USD', forecast: '220K', previous: '222K', affectedPairs: ['ES', 'NQ', 'YM'] },
  { id: 'e2', date: '2026-07-24T14:00:00Z', time: '10:00 ET', title: 'Existing Home Sales',    impact: 'medium', currency: 'USD', forecast: '0.5%', previous: '-0.7%', affectedPairs: ['ES'] },
  { id: 'e3', date: '2026-07-24T18:00:00Z', time: '14:00 ET', title: 'Fed Chair Powell Speaks', impact: 'high',   currency: 'USD', affectedPairs: ['ES', 'NQ', 'YM', 'GC', 'CL'] },
  { id: 'e4', date: '2026-07-25T12:30:00Z', time: '08:30 ET', title: 'Core Durable Goods',      impact: 'high',   currency: 'USD', forecast: '0.3%', previous: '0.1%', affectedPairs: ['ES', 'NQ'] },
  { id: 'e5', date: '2026-07-25T12:30:00Z', time: '08:30 ET', title: 'GDP (QoQ, advance)',      impact: 'high',   currency: 'USD', forecast: '2.0%', previous: '1.4%', affectedPairs: ['ES', 'NQ', 'YM'] },
  { id: 'e6', date: '2026-07-25T18:00:00Z', time: '14:00 ET', title: 'FOMC Member Daly Speaks', impact: 'medium', currency: 'USD', affectedPairs: ['ES', 'NQ'] },
  { id: 'e7', date: '2026-07-26T12:30:00Z', time: '08:30 ET', title: 'Core PCE (YoY)',          impact: 'high',   currency: 'USD', forecast: '2.5%', previous: '2.6%', affectedPairs: ['ES', 'NQ', 'GC'] },
  { id: 'e8', date: '2026-07-26T18:00:00Z', time: '14:00 ET', title: 'Fed Chair Powell Speaks', impact: 'high',   currency: 'USD', affectedPairs: ['ES', 'NQ', 'YM', 'GC', 'CL'] },
];

const IMPACT_META = {
  high:   { color: '#FF3B30', label: 'HIGH IMPACT',    width: 3, dot: 'bg-tf-loss' },
  medium: { color: '#FF9500', label: 'MEDIUM IMPACT',  width: 2, dot: 'bg-tf-orange' },
  low:    { color: '#34C759', label: 'LOW IMPACT',     width: 1, dot: 'bg-tf-green' },
};

export function NewsBlackoutsPage() {
  const nav = useNavigate();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const next = EVENTS.find((e) => new Date(e.date).getTime() > now.getTime());
  const timeToNext = next ? new Date(next.date).getTime() - now.getTime() : 0;
  const mins = Math.floor(timeToNext / 60000);
  const secs = Math.floor((timeToNext % 60000) / 1000);
  const inBlackout = next ? timeToNext < 2 * 60 * 1000 && timeToNext > 0 : false;
  const highImpactCount = EVENTS.filter(e => e.impact === 'high').length;
  const mediumImpactCount = EVENTS.filter(e => e.impact === 'medium').length;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">News blackouts</div>
        <button className="px-3 text-[15px] text-tf-blue">Subscribe</button>
      </div>

      {/* Hero — countdown or blackout */}
      <div className="mx-4 mt-4">
        {inBlackout ? (
          <div
            className="rounded-[24px] p-4 text-center"
            style={{ background: 'linear-gradient(135deg, #FF3B30 0%, #B82B22 100%)', color: 'white', boxShadow: '0 12px 40px rgba(0,0,0,0.08)' }}
            data-testid="blackout-active"
          >
            <div className="text-[13px]" style={{ color: 'rgba(255,255,255,0.85)' }}>🚫 Blackout window active</div>
            <div className="mt-1 text-[40px] font-bold tabular-nums leading-none">NO TRADING</div>
            <div className="text-[12px] mt-1" style={{ color: 'rgba(255,255,255,0.9)' }}>2-min rule · Section 13.9</div>
            <p className="mt-2 text-[11px] max-w-xs mx-auto" style={{ color: 'rgba(255,255,255,0.85)' }}>High-impact news event: <span className="font-bold">{next?.title}</span></p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button onClick={() => alert('Bot auto-paused')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-white text-[15px] font-semibold text-tf-loss active:scale-[0.97] transition-transform">
                Pause bot
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7" /><path d="M7 7h10v10" />
                </svg>
              </button>
              <button onClick={() => nav(-1)} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-white text-[15px] font-semibold text-tf-loss active:scale-[0.97] transition-transform">
                Back
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 7L7 17" /><path d="M17 17H7V7" />
                </svg>
              </button>
            </div>
            <div className="mt-3 rounded-[10px] bg-white/15 backdrop-blur-md p-2 text-[11px]">
              Bot auto-paused · {secs}s remaining
            </div>
          </div>
        ) : (
          <TfCardFloating className="text-center">
            <div className="text-[13px]" style={{ color: '#8E8E93' }}>Next high-impact event</div>
            <div className="mt-1 flex items-baseline justify-center gap-1">
              <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>
                {next ? `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}:${String(secs).padStart(2, '0')}` : '--:--'}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
              <span className="text-tf-text-3 truncate">{next?.title} · {next?.time}</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button onClick={() => alert('Subscribed to alerts')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
                Subscribe
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7" /><path d="M7 7h10v10" />
                </svg>
              </button>
              <button onClick={() => nav('/portal/auto-flatten')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
                Auto-flatten
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 7L7 17" /><path d="M17 17H7V7" />
                </svg>
              </button>
            </div>
            {next && mins < 5 && <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-tf-orange px-2.5 py-1 text-[10px] font-bold uppercase animate-pulse">⚠️ Blackout in {mins}m</div>}
          </TfCardFloating>
        )}
      </div>

      {/* 3-up stat row */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'High impact', value: `${highImpactCount}`, color: '#FF3B30' },
          { label: 'Medium', value: `${mediumImpactCount}`, color: '#FF9500' },
          { label: 'Blackout', value: '2 min', color: '#1C1C1E' },
        ]}
      />

      {/* The 2-min rule explainer */}
      <div className="mx-4 mt-3">
        <TfCard className="p-4">
          <h2 className="text-[14px] font-bold text-tf-text-1">The 2-minute rule</h2>
          <p className="mt-1.5 text-[13px] text-tf-text-2 leading-relaxed">
            Per Section 13.9, you cannot open or close trades within <span className="font-bold text-tf-loss">2 minutes</span> of any high-impact news event. The bot auto-pauses during this window.
          </p>
        </TfCard>
      </div>

      {/* Today's events */}
      <TfSectionHeaderView title="Upcoming events" viewAllLabel="View all" onViewAll={() => {}} />
      <div className="mx-4">
        <TfCard>
          {EVENTS.map((e, i) => {
            const m = IMPACT_META[e.impact];
            return (
              <div key={e.id} data-testid={`event-${e.id}`} className={['flex items-start gap-3 px-4 py-3', i < EVENTS.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
                <div className="w-16 shrink-0 text-center">
                  <div className="text-[11px] text-tf-text-3 tabular-nums">{e.time}</div>
                  <div className="text-[10px] font-bold uppercase mt-0.5" style={{ color: m.color }}>{e.currency}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[14px] font-semibold text-tf-text-1 truncate">{e.title}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className={['h-1.5 w-1.5 rounded-full', m.dot].join(' ')} />
                    <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: m.color }}>{m.label}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10px]">
                    {e.forecast && <span className="text-tf-text-3">F <span className="text-tf-text-1 font-semibold">{e.forecast}</span></span>}
                    {e.previous && <span className="text-tf-text-3">P <span className="text-tf-text-1 font-semibold">{e.previous}</span></span>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {e.affectedPairs.map((p) => <span key={p} className="rounded-full bg-tf-surface-1 px-1.5 py-0.5 text-[9px] font-bold text-tf-text-3">{p}</span>)}
                  </div>
                </div>
              </div>
            );
          })}
        </TfCard>
      </div>

      {/* How bot handles */}
      <TfSectionHeaderView title="How your bot handles this" />
      <div className="mx-4">
        <TfCard>
          {[
            'Auto-pauses 2 min before high-impact news',
            'Closes all open positions 30s before event',
            'Re-enables 2 min after event',
            'Medium-impact events: warning only, no pause',
          ].map((step, i, a) => (
            <div key={i} data-testid={`bot-handle-${i}`} className={['flex items-center gap-3 px-4 py-3', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-tf-green-tint text-tf-profit text-[14px]">✓</div>
              <div className="text-[13px] text-tf-text-1">{step}</div>
            </div>
          ))}
        </TfCard>
      </div>
    </div>
  );
}
